/**
 * Profile Page
 * Full profile detail view with photo gallery, info, achievements, and tabbed content
 */

import { Search, X } from "lucide-react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useAuthStore } from "@/features/auth/store/authStore"
import { CoachingTabContent } from "../components/CoachingTabContent"
import { CommunitiesTabContent } from "../components/CommunitiesTabContent"
import { FollowersTabContent } from "../components/FollowersTabContent"
import { FollowingTabContent } from "../components/FollowingTabContent"
import { PracticingTabContent } from "../components/PracticingTabContent"
import { ProfileInfoSectionDetail } from "../components/ProfileInfoSection.detail"
import { ProfilePhotoGallery } from "../components/ProfilePhotoGallery"
import { TeachingTabContent } from "../components/TeachingTabContent"
import { useFollowRealtime } from "../hooks/useFollowRealtime"
import { usePublicProfileStatistics } from "../hooks/usePublicProfileStatistics"
import type { ProfileDetail } from "../types/profile.types"

type ProfileTab = "teaching" | "practicing" | "sessions" | "communities" | "followers" | "following"

export function ProfileScreen() {
  const { t } = useTranslation("profile")
  const [activeTab, setActiveTab] = useState<ProfileTab>("teaching")
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const tabsRef = useRef<HTMLDivElement>(null)
  const user = useAuthStore(state => state.user)

  useFollowRealtime()

  // Fetch profile statistics from API (Active + Upcoming only for profile page)
  const { data: statsResponse } = usePublicProfileStatistics(user?.id || "")
  const statistics = statsResponse?.data

  // The native profile video is fetched + rendered by ProfileInfoSectionDetail
  // (via the userId prop). introVideoUrl carries only the legacy URL, if any.
  const introVideoUrl = user?.introVideoUrl

  // Handle null user case
  if (!user) {
    return <div>{t("loading")}</div>
  }

  const handleFollowersClick = () => {
    setActiveTab("followers")
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 100)
  }

  const handleFollowingClick = () => {
    setActiveTab("following")
    setTimeout(() => {
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    }, 100)
  }

  // Build profile data from auth store
  const profile: ProfileDetail = {
    id: user.id,
    slug: user.slug,
    name: user.name || "User",
    username: user.username || "",
    bio: user.bio || "",
    city: user.city || "",
    state: user.state || "",
    credentials: [], // Not available in database
    tags: user.tags?.map(t => t.name) || [],
    photos: (() => {
      if (!user.profile_photos || user.profile_photos.length === 0) {
        return []
      }

      const coverPhoto = user.profile_photos.find(p => p.is_cover_image || p.isCover)
      const otherPhotos = user.profile_photos
        .filter(p => !(p.is_cover_image || p.isCover))
        .map(p => p.filePath)
        .slice(0, 2)

      return [...(coverPhoto ? [coverPhoto.filePath] : []), ...otherPhotos]
    })(),
    profilePhoto: user.profile_photos?.find(p => p.is_cover_image || p.isCover)?.filePath,
    galleryPhotos:
      user.profile_photos
        ?.filter(p => {
          // Filter out videos - only show images in gallery (handle both snake_case and camelCase)
          const fileType = p.file_type || (p as unknown as { fileType?: string }).fileType
          const isVideo = fileType?.startsWith("video")
          // Exclude cover photos and videos
          const isCover = p.is_cover_image || p.isCover
          return !isVideo && !isCover
        })
        .map(p => p.filePath)
        .slice(0, 3) || [],
    followersCount: statistics?.followersCount ?? 0,
    followingCount: statistics?.followingCount ?? 0,
    socials: {
      website: user.website_url || "",
      twitter: user.twitter_url || "",
      instagram: user.instagram_url || "",
      facebook: user.facebook_url || "",
    },
    // Use real statistics from API, or show 0 while loading
    practicingCount: statistics?.practicing ?? 0,
    teachingCount: statistics?.teachings ?? 0,
    coachingCount: statistics?.coachings?.created ?? 0,
    communitiesCount: statistics?.communities ?? 0,
  }

  return (
    <div className="bg-background min-h-screen md:pt-8 md:mt-3">
      <div className="max-w-360 mx-auto px-5 md:px-17.5">
        {/* Photo Gallery */}
        <ProfilePhotoGallery photos={profile.galleryPhotos} className="mt-1" />

        {/* Profile Info + Socials */}
        <ProfileInfoSectionDetail
          profile={profile}
          className="mt-7.5 md:mt-11"
          userId={user?.id}
          introVideoUrl={introVideoUrl}
          onFollowersClick={handleFollowersClick}
          onFollowingClick={handleFollowingClick}
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as ProfileTab)} className="mt-5">
        <div ref={tabsRef} className=" max-w-360 mx-auto px-5 md:px-17.5 w-full">
          <div className={`flex md:border-b-1 md:items-center md:justify-between`}>
            <div className="overflow-x-auto overflow-y-hidden -mx-5 px-5 lg:mx-0 lg:px-0 scrollbar-hide">
              <TabsList
                variant="default"
                className={`!h-auto text-body gap-0 p-0 flex-nowrap lg:flex-wrap lg:overflow-visible`}
              >
                <TabsTrigger
                  value="teaching"
                  className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                >
                  {t("teaching.title")} ({profile.teachingCount})
                </TabsTrigger>
                <TabsTrigger
                  value="practicing"
                  className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                >
                  {t("practicing.title")} ({profile.practicingCount})
                </TabsTrigger>
                <TabsTrigger
                  value="sessions"
                  className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                >
                  {t("coaching.title")} ({profile.coachingCount ?? 0})
                </TabsTrigger>
                <TabsTrigger
                  value="communities"
                  className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                >
                  {t("communities.title")} ({profile.communitiesCount})
                </TabsTrigger>
                <TabsTrigger
                  value="followers"
                  className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                >
                  {t("followers")} ({profile.followersCount})
                </TabsTrigger>
                <TabsTrigger
                  value="following"
                  className="rounded-none py-3 md:py-6 md:px-4 pl-5 pr-2.5 shrink-0 justify-center text-[13px] font-medium data-[state=active]:font-semibold data-[state=active]:text-charcoal data-[state=active]:border-b-1 border-0 data-[state=active]:border-white cursor-pointer uppercase"
                >
                  {t("followingTab.title")} ({profile.followingCount})
                </TabsTrigger>
              </TabsList>
            </div>
            <div className="hidden lg:flex items-center md:justify-end">
              {/* Always show search bar on small screens, toggle on md+ */}
              <div className="hidden md:flex items-center md:justify-end">
                {showSearch ? (
                  <div className="mt-5 md:mt-0 flex items-center gap-2 rounded-sm border border-card bg-card px-3 py-1.5 w-full md:w-auto">
                    <Search className="w-4 h-4 text-ash shrink-0" />
                    <Input
                      type="text"
                      placeholder={t("profile.searchPlaceholder")}
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="w-full border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-6 w-6"
                      onClick={() => {
                        setShowSearch(false)
                        setSearchQuery("")
                      }}
                      aria-label="Close search"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() => setShowSearch(true)}
                    aria-label="Search"
                  >
                    <Search className="w-5 h-5 text-body" />
                  </Button>
                )}
              </div>
              {/* Always visible search bar on small screens */}
              <div className="flex md:hidden items-center gap-2 w-full mt-10">
                <div className="flex items-center gap-2 rounded-sm border border-card bg-card px-3 py-1.5 w-full">
                  <Search className="w-4 h-4 text-ash shrink-0" />
                  <Input
                    type="text"
                    placeholder={t("profile.searchPlaceholder")}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full border-none bg-transparent px-2 text-sm shadow-none focus-visible:ring-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:bg-card bg-transparent">
          <div className="max-w-360 mx-auto w-full md:px-17.5 py-5 md:py-7">
            <TabsContent value="teaching">
              <TeachingTabContent userId={user.id} searchQuery={searchQuery} />
            </TabsContent>

            <TabsContent value="practicing">
              <PracticingTabContent userId={user.id} searchQuery={searchQuery} />
            </TabsContent>

            <TabsContent value="sessions">
              <CoachingTabContent userId={user.id} searchQuery={searchQuery} showHeader={false} />
            </TabsContent>

            <TabsContent value="communities">
              <CommunitiesTabContent
                totalCount={profile.communitiesCount}
                userId={user?.id}
                searchQuery={searchQuery}
              />
            </TabsContent>

            <TabsContent value="followers">
              <FollowersTabContent
                userId={user.id}
                searchQuery={searchQuery}
                headerTitle={t("followers")}
              />
            </TabsContent>

            <TabsContent value="following">
              <FollowingTabContent
                userId={user.id}
                searchQuery={searchQuery}
                headerTitle={t("followingTab.title")}
              />
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  )
}
